import React, { useState, useEffect } from 'react';
import Modal from 'global/components/modal';
import {
  TmContainer,
  RealmResetDiv,
  Header,
  Grouping,
  NoneYet,
  FlexRow,
  TemplateButtonStyled,
  TemplateButton2,
} from './styles';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Icon, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { get, size, set } from 'global/lib/lodash';
import setUserRealm from 'redux/actions/userRealm';
import { userHasPermissions, getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from 'feature/admin/account-settings';
import Styles, { Loader, useScreen } from 'global/styles';
import Alert from '@mui/material/Alert';
import { withRouter } from 'react-router-dom';
import { useUser } from 'global/hooks/useUser';
import { getOnlyTiersFromScorecards } from '../index';
import { Text } from '../../../../global/components/text';
import useTemplateLocationSteps, { defaultAssessmentName } from '../../template-location-steps';

const TemplateButton = (props) => {
  const { title, secondButton, onClick, subtitle } = props;

  return (
    <FlexRow>
      <TemplateButtonStyled startIcon={<Icon>add</Icon>} onClick={onClick}>
        <div style={{ textAlign: 'left' }}>
          <div>{title}</div>
          {!!subtitle && (
            <div>
              <Text type="label_small" italic>{`for ${subtitle}`}</Text>
            </div>
          )}
        </div>
      </TemplateButtonStyled>
      {secondButton && (
        <TemplateButton2 as={TemplateButtonStyled} onClick={secondButton.onClick}>
          <Icon>{secondButton.icon}</Icon>
        </TemplateButton2>
      )}
    </FlexRow>
  );
};

const RealmReset = (props) => {
  const realm = useSelector((state) => get(state, 'auth.realm'));
  const dispatch = useDispatch();

  const onReset = () => {
    dispatch(setUserRealm(null));
  };

  const hasRealm = get(realm, 'id');

  return !hasRealm ? null : (
    <RealmResetDiv>
      Showing available templates for <Icon>room</Icon>
      {get(realm, 'name')} <Button onClick={onReset}>RESET</Button>
    </RealmResetDiv>
  );
};

export default withRouter((props) => {
  const { templates, onDefaultSelect, startTemplateCreate } = props;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [selected, setSelected] = useState();
  const { isMobileXS } = useScreen();
  const { me } = useUser();
  const realm = useSelector((state) => get(state, 'auth.realm'));
  const styles = Styles();

  const hasPerm = userHasPermissions('createReportTemplates');
  const customAllowed = getAccountSetting(SETTINGS.customAssessmentsAllowed) != '0';
  const showCustom = customAllowed || hasPerm;

  const onEdit = (temp) => {
    props.history.push(`assessment-template/${temp.id}`);
  };

  const submitTemplate = async (r, values = {}) => {
    try {
      setLoading(true);
      const scorecards = {};
      Object.keys(get(selected, 'scores') || {}).forEach((k) => {
        set(scorecards, `scorecard_${k}`, k);
      });
      const err =
        props.onSelectTemplate &&
        (await props.onSelectTemplate({
          ...selected,
          ...scorecards,
          generalVisibility: get(selected, 'visibility'),
          template_id: selected.id,
          realmId: r.id,
          accessRealmTier: get(r, 'tier', 'division'),
          reportName: defaultAssessmentName(r, selected),
          ...values,
        }));

      if (err) {
        setError(err.message);
      }
    } catch (err) {
      console.error('submitTemplate failed', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (temp) => {
    setSelected(temp);
    setError();
  };

  const resetToTemplateList = () => {
    setSelected();
    setError();
  };

  useEffect(() => {
    if (props.open) {
      setSelected();
      setError();
      setLoading(false);
    }
  }, [props.open]);

  const all = get(templates, 'data', []);
  const global = all.filter((t) => get(t, 'status') === 'global_template');
  const mine = all.filter((t) => get(t, 'status') === 'template' && (get(t, 'authors', []).find((u) => u.id === me.id) || get(t, 'editors', []).find((u) => u.id === me.id)));

  const hasMine = size(mine) > 0;

  const onlyTiers = (() => {
    if (!selected) {
      return ['division', 'community', 'lot'];
    }

    return getOnlyTiersFromScorecards(get(selected, 'scorecards', []));
  })();

  const { content: locationContent, buttons: locationButtons } = useTemplateLocationSteps({
    template: selected,
    currentRealm: realm,
    onlyTiers,
    onCancel: resetToTemplateList,
    onSubmit: submitTemplate,
    disabled: loading,
  });

  const showLocationSteps = !!selected && !loading;

  return (
    <Modal
      title={selected ? `New ${get(selected, 'name') || ''}` : 'Create Assessment'}
      {...props}
      fullHeight={!!selected}
      buttons={showLocationSteps ? locationButtons : undefined}
    >
      {loading && (
        <TmContainer isMobileXS={isMobileXS} className={styles.centerColumn}>
          <Loader />
        </TmContainer>
      )}

      {showLocationSteps && (
        <>
          {error && (
            <Alert
              severity="error"
              action={
                <IconButton aria-label="close" color="inherit" size="small" onClick={() => setError()}>
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}
          {locationContent}
        </>
      )}

      {!loading && !selected && (
        <TmContainer isMobileXS={isMobileXS}>
          {error && (
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setError();
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}
          {showCustom && <TemplateButton title="Custom Assessment" onClick={onDefaultSelect} />}

          <Grouping>
            <RealmReset />
            {!!size(global) && (
              <>
                <Header>All Templates</Header>
                {global.map((temp, i) => {
                  const canEdit =
                    get(temp, 'authors', []).find((u) => u.id === me.id) || get(temp, 'editors', []).find((u) => u.id === me.id);
                  return (
                    <TemplateButton
                      key={i}
                      onClick={() => selectTemplate(temp)}
                      title={get(temp, 'name')}
                      subtitle={get(temp, 'realm.name')}
                      secondButton={canEdit ? { icon: 'edit', onClick: () => onEdit(temp) } : undefined}
                    />
                  );
                })}
              </>
            )}
          </Grouping>

          {(hasPerm || hasMine) && (
            <Grouping>
              <FlexRow>
                <Header>My Templates</Header>
                {hasPerm && (
                  <TemplateButton2 as={TemplateButtonStyled} onClick={startTemplateCreate}>
                    <Icon>add</Icon>
                  </TemplateButton2>
                )}
              </FlexRow>
              {mine.map((temp, i) => (
                <TemplateButton
                  key={i}
                  onClick={() => selectTemplate(temp)}
                  title={get(temp, 'name')}
                  subtitle={get(temp, 'realm.name')}
                  secondButton={{ icon: 'edit', onClick: () => onEdit(temp) }}
                />
              ))}
              {!hasMine && <NoneYet>{`You haven't created any assessment templates yet`}</NoneYet>}
            </Grouping>
          )}
        </TmContainer>
      )}
    </Modal>
  );
});
